import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Paper, Typography, Divider } from '@mui/material';
import AddCommentIcon from '@mui/icons-material/AddComment';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useSelector } from 'react-redux';

const CommentsComponent = ({ properties = {}, id, value, valueUpdaterCallback, validityUpdaterCallback }) => {
    const { options = {}, constrains = {} } = properties;
    const { required = false, label = "Comments", username = "Anonymous" } = options;
    const { maxComments, minComments, maxCommentLength = 500 } = constrains;

    const [comments, setComments] = useState(value || []);
    const [newCommentText, setNewCommentText] = useState('');
    const [error, setError] = useState(false);
    const [helperText, setHelperText] = useState('');

    const currentUser = useSelector((state) => state.UserMetaData.fullName || "Anonymous");
    const currentUserProfileImage = useSelector((state) => state.UserMetaData.profileImage);

    // Sync with parent value
    useEffect(() => {
        if (value !== undefined && value !== comments) {
            setComments(value || []);
        }
    }, [value]);

    // Validate comments
    const validate = (commentsList) => {
        let isValid = true;
        let errorMessage = '';

        if (required && commentsList.length === 0) {
            isValid = false;
            errorMessage = 'At least one comment is required';
        } else if (minComments && commentsList.length < minComments) {
            isValid = false;
            errorMessage = `Minimum ${minComments} comments required`;
        } else if (maxComments && commentsList.length > maxComments) {
            isValid = false;
            errorMessage = `Maximum ${maxComments} comments allowed`;
        }

        setError(!isValid);
        setHelperText(errorMessage);

        if (validityUpdaterCallback) {
            validityUpdaterCallback(isValid);
        }

        return isValid;
    };

    const handleAddComment = () => {
        if (!newCommentText.trim()) {
            return;
        }

        if (maxComments && comments.length >= maxComments) {
            setHelperText(`Maximum ${maxComments} comments reached`);
            return;
        }

        const newComment = {
            id: Date.now(),
            text: newCommentText.trim(),
            username: currentUser || username,
            profileImage: currentUserProfileImage,
            timestamp: new Date().toISOString(),
            displayDate: new Date().toLocaleString(),
        };

        const updatedComments = [...comments, newComment];
        setComments(updatedComments);
        setNewCommentText('');
        validate(updatedComments);

        if (valueUpdaterCallback) {
            valueUpdaterCallback(updatedComments);
        }
        console.log("Added comment:", newComment);
    };

    const handleDeleteComment = (commentId) => {
        const updatedComments = comments.filter(comment => comment.id !== commentId);
        setComments(updatedComments);
        validate(updatedComments);

        if (valueUpdaterCallback) {
            valueUpdaterCallback(updatedComments);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            handleAddComment();
        }
    };

    return (
        <Box sx={{ width: '100%', maxWidth: '100%' }}>
            <Typography variant="h6" gutterBottom>
                {label}
                {required && <span style={{ color: 'red' }}> *</span>}
            </Typography>

            {/* Add Comment Section */}
            <Box display="flex" gap={1} mb={2}>
                <TextField
                    multiline
                    rows={2}
                    value={newCommentText}
                    onChange={(e) => setNewCommentText(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Write a comment... (Ctrl+Enter to submit)"
                    fullWidth
                    variant="outlined"
                    error={error}
                    helperText={helperText}
                    inputProps={{
                        maxLength: maxCommentLength,
                    }}
                />
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddComment}
                    disabled={!newCommentText.trim()}
                    startIcon={<AddCommentIcon />}
                    style={{ minWidth: '120px', height: 'fit-content' }}
                >
                    Add
                </Button>
            </Box>

            {/* Character Counter */}
            {maxCommentLength && (
                <Typography variant="caption" color="textSecondary" style={{ marginBottom: '10px', display: 'block' }}>
                    {newCommentText.length}/{maxCommentLength} characters
                </Typography>
            )}

            {/* Comments Display Counter */}
            <Typography variant="body2" color="textSecondary" gutterBottom>
                {comments.length} comment{comments.length !== 1 ? 's' : ''}
                {minComments && ` (minimum: ${minComments})`}
                {maxComments && ` (maximum: ${maxComments})`}
            </Typography>

            {/* Comments List */}
            <Box style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {comments.length === 0 ? (
                    <Paper style={{ padding: '20px', textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                        <Typography variant="body2" color="textSecondary">
                            No comments yet. Be the first to comment!
                        </Typography>
                    </Paper>
                ) : (
                    comments.map((comment, index) => (
                        <Paper
                            key={comment.id}
                            elevation={2}
                            style={{
                                padding: '15px',
                                marginBottom: '10px',
                                backgroundColor: '#fafafa',
                            }}
                        >
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                <Box flex={1}>
                                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            {comment.profileImage ? (
                                                <Box
                                                    component="img"
                                                    src={comment.profileImage}
                                                    alt={comment.username}
                                                    sx={{
                                                        width: 24,
                                                        height: 24,
                                                        borderRadius: '50%',
                                                        objectFit: 'cover',
                                                        border: '1px solid #ddd'
                                                    }}
                                                />
                                            ) : (
                                                <PersonIcon fontSize="small" color="primary" />
                                            )}
                                            <Typography variant="subtitle2" color="primary">
                                                {comment.username}
                                            </Typography>
                                        </Box>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <AccessTimeIcon fontSize="small" color="action" />
                                            <Typography variant="caption" color="textSecondary">
                                                {comment.displayDate}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {comment.text}
                                    </Typography>
                                </Box>
                                <Button
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteComment(comment.id)}
                                    style={{ minWidth: '60px' }}
                                >
                                    Delete
                                </Button>
                            </Box>
                            {index < comments.length - 1 && <Divider style={{ marginTop: '10px' }} />}
                        </Paper>
                    ))
                )}
            </Box>
        </Box>
    );
};

export default CommentsComponent;
